import React, { useState, useEffect, useCallback } from "react";
import { WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { api } from "@/api/client";
import { useGlobal } from "@/components/contexts/GlobalContext";
import { reconnectReasonMessage } from "@/lib/oauthErrorCopy";

const PLATFORM_LABELS = {
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  linkedin_ads: "LinkedIn Ads",
};

const DISMISS_KEY_PREFIX = "reconnect_banner_dismissed_at";
const DISMISS_TTL_MS = 60 * 60 * 1000; // 1 hour — re-show after 1 hour

function dismissKey(profileId) {
  return profileId
    ? `${DISMISS_KEY_PREFIX}:${profileId}`
    : DISMISS_KEY_PREFIX;
}

/**
 * Profile-scoped reconnect banner (Wave 11.3 R-E).
 * Uses current profile from GlobalContext; falls back to selectedProfileId in localStorage.
 */
export default function ReconnectBanner() {
  const navigate = useNavigate();
  const { currentProfile } = useGlobal();
  const profileId =
    currentProfile?.id ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("selectedProfileId")
      : null);

  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    if (!profileId) {
      setItems([]);
      return;
    }

    try {
      const res = await api.profiles.getConnections(profileId);
      const connections = res.data?.connections || [];
      const needing = [];

      for (const c of connections) {
        const acc = c.account || {};
        if (acc.needs_reconnect !== true) continue;

        const reasonCode = acc.reconnect_reason || acc.reason || null;
        const reasonText = reconnectReasonMessage(/** @type {string | null | undefined} */ (reasonCode));

        needing.push({
          key: c.platform,
          reason: reasonText,
        });
      }

      // Also check company OAuth status with profileId when available
      if (needing.length === 0) {
        const results = await Promise.allSettled([
          api.oauth.google.status(profileId),
          api.oauth.meta.status(profileId),
          api.oauth.linkedin.status(profileId),
        ]);
        const keys = ["google_ads", "meta_ads", "linkedin_ads"];
        results.forEach((result, i) => {
          if (
            result.status === "fulfilled" &&
            result.value?.data?.needs_reconnect
          ) {
            const reasonCode =
              result.value.data.reconnect_reason ||
              result.value.data.reason ||
              null;
            needing.push({
              key: keys[i],
              reason: reconnectReasonMessage(reasonCode),
            });
          }
        });
      }

      setItems(needing);
    } catch {
      setItems([]);
    }
  }, [profileId]);

  useEffect(() => {
    setDismissed(false);
    void check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [check, profileId]);

  const wasDismissed = (() => {
    try {
      const ts = sessionStorage.getItem(dismissKey(profileId));
      if (!ts) return false;
      return Date.now() - parseInt(ts, 10) < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  })();

  if (dismissed || wasDismissed || items.length === 0) return null;

  const platformNames = items
    .map((p) => PLATFORM_LABELS[p.key] || p.key)
    .join(", ");
  const reasonLine = items.map((p) => p.reason).find(Boolean);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey(profileId), Date.now().toString());
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <WifiOff className="w-4 h-4 flex-shrink-0" />
        <p className="text-xs">
          <strong>Reconnect required:</strong> {platformNames}
          {reasonLine ? ` — ${reasonLine}` : " —"}{" "}
          <button
            type="button"
            onClick={() => navigate(createPageUrl("Integrations"))}
            className="underline hover:no-underline font-medium"
          >
            reconnect now
          </button>
          {!reasonLine ? " to resume data syncing." : null}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-current hover:text-white flex-shrink-0"
        onClick={handleDismiss}
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}
